/**
 * I QUATTRO esiti dell'emissione su Fatture in Cloud, e cosa legge l'artigiano.
 *
 * PERCHÉ UN MODULO A PARTE: è la parte che decide cosa vede chi ha appena
 * premuto un bottone IRREVERSIBILE, ed è l'unica cosa da provare. Dentro al
 * componente servirebbe montarlo con una libreria che il progetto non ha.
 * Qui è una funzione pura: la prova costa venti righe e non tocca il DOM.
 * (Stessa forma di `mfaTesti.js`, e per la stessa ragione.)
 *
 * ⛔ LA DECISIONE SI PRENDE SU UN CAMPO, MAI SU UNA FRASE. Il backend manda
 * `codice` e `riprova_sicura`: una diagnosi che dipende da una stringa italiana
 * si rompe in silenzio la prima volta che qualcuno riscrive il messaggio.
 *
 * 🔴 E IL QUARTO ESITO È QUELLO CHE COSTA DI PIÙ SBAGLIARE. «Non so com'è
 * andata» non è «non è andata»: il documento POTREBBE essere su FIC. Un bottone
 * «Riprova» lì dentro è il modo in cui si fanno DUE fatture per lo stesso
 * lavoro — non è teorico, è successo il 28/07. Per questo `riprova` è una
 * proprietà dell'esito e non una decisione del componente.
 */

// Ripiego: un codice che oggi non esiste non deve lasciare lo schermo vuoto,
// e non deve nemmeno diventare un invito a riprovare per sbaglio.
export const RIPIEGO = {
  tipo: 'errore',
  titolo: 'Non sono riuscito a inviarla',
  azione: 'Riprova',
  riprova: true,
};

// I due muri hanno una forma loro perché chiedono all'artigiano DUE cose
// diverse: il primo un dato che possiamo raccogliere qui, il secondo un giro
// fuori da mAIstro. Un muro che chiede è un passo; un muro che rimanda è
// un'uscita — e la differenza costa un campo di input.
export const MURO_DATO_MANCANTE = 'cliente_non_agganciabile';
export const MURO_FUORI_APP = 'cliente_non_su_fic';

// ⛔ Il muro dei cento documenti. Non e' un muro di chi comincia: e' l'unico
// che arriva come CONSEGUENZA DEL SUCCESSO — colpisce l'artigiano che ha
// fatturato cento volte, quello che rinnova ([[DBT-118]]).
export const PLAFOND_DOCUMENTI = 'plafond_documenti_esaurito';

const BLOCCHI = {
  [MURO_DATO_MANCANTE]: {
    tipo: 'chiede',
    titolo: 'Mi serve la partita IVA del cliente',
    spiega:
      'Per emettere una fattura elettronica serve la partita IVA o il codice ' +
      'fiscale del cliente. Scrivilo qui: lo salvo sulla sua scheda e continuo.',
    azione: 'Salva e riprova',
    riprova: true,
  },
  [PLAFOND_DOCUMENTI]: {
    tipo: 'bloccata',
    titolo: 'Il piano Fatture in Cloud e’ esaurito',
    spiega:
      'Il tuo piano Fatture in Cloud ha esaurito i documenti disponibili. Per '
      + 'emettere altre fatture devi aggiornare il piano su Fatture in Cloud.',
    azione: 'Apri Fatture in Cloud',
    riprova: false,
  },
  [MURO_FUORI_APP]: {
    tipo: 'devia',
    titolo: 'Il cliente non è ancora su Fatture in Cloud',
    spiega:
      'Crealo lì con la stessa partita IVA, poi torna qui: la fattura resta ' +
      'com’è, non devi rifare niente.',
    azione: 'Ho fatto, ricontrolla',
    riprova: true,
  },
  connessione_non_attiva: {
    tipo: 'devia',
    titolo: 'Fatture in Cloud non è collegato',
    spiega: 'Vai in Impostazioni › Integrazioni e collega il tuo account.',
    azione: 'Vai alle integrazioni',
    riprova: false,
  },
};


// ── GLI INDIRIZZI DEL PANNELLO FATTURE IN CLOUD ───────────────────────────
//
// 🔴 COPIATI DALLA BARRA DEL BROWSER IL 02/09/2026, NON DEDOTTI. La differenza
// non è pedanteria: la costante che c'era prima diceva `/entities-clients` —
// il nome della RISORSA DELL'API (`/c/{id}/entities/clients`) usato come
// percorso del PANNELLO — e il bottone del muro ② ci portava l'artigiano
// mentre era bloccato. Misurato: il percorso vero è `/clients`.
//
// ⚠️ E il fossile che l'aveva prodotta: l'indirizzo della lista si otteneva
// con `URL_FIC_CLIENTI.replace('entities-clients', 'invoices')`. Un indirizzo
// COPIATO si presenta come una stringa; uno DEDOTTO come un'operazione su
// un'altra stringa. Quel `.replace()` restituiva `/invoices`, che è il valore
// GIUSTO derivato da una base SBAGLIATA — giusto per fortuna, non per metodo.
// Vedi [[DBT-117]].
const PANNELLO_FIC = 'https://secure.fattureincloud.it';

export const URL_FIC_CLIENTI = `${PANNELLO_FIC}/clients`;
export const URL_FIC_DOCUMENTI = `${PANNELLO_FIC}/invoices`;

/**
 * L'indirizzo del documento dentro il pannello, o la lista se non c'è l'id.
 *
 * ⚠️ QUESTA FORMA NON È DOCUMENTATA DA FATTURE IN CLOUD. È stata misurata una
 * volta, il 02/09/2026, sulla fattura 549700991:
 *     https://secure.fattureincloud.it/invoices/view/549700991
 * Se FIC rifà il pannello, questo indirizzo si rompe — e si romperebbe in
 * SILENZIO, portando l'artigiano su una pagina che non c'è.
 *
 * ⛔ Per questo la caduta non è un dettaglio: senza `fic_document_id` si va
 * alla LISTA, che è un posto onesto da cui l'azione si può comunque
 * completare. Il costo della rottura resta un click, non un vicolo cieco.
 *
 * ⛔ E MAI il campo `url` che l'API restituisce sul documento: quello è il
 * DOCUMENTO DI CORTESIA (`compute.fattureincloud.it/doc/<token>`), la stampa
 * per il cliente — non il pannello dove si firma e si invia. Aprirlo
 * sembrerebbe funzionare, e direbbe all'artigiano che ha finito.
 */
export function urlDocumentoFIC(ficDocumentId) {
  return ficDocumentId
    ? `${URL_FIC_DOCUMENTI}/view/${ficDocumentId}`
    : URL_FIC_DOCUMENTI;
}

/**
 * Traduce la risposta del backend in ciò che l'artigiano legge.
 *
 * @param {number} status  codice HTTP
 * @param {object} data    corpo della risposta (`codice`, `riprova_sicura`, …)
 */
export function esitoEmissione(status, data) {
  const d = data || {};

  // ── ④ NON SO COM'È ANDATA — prima di tutti gli altri, perché è quello che
  // non deve mai cadere in un ramo che offre il riprova.
  if (d.codice === 'esito_incerto' || d.riprova_sicura === false) {
    return {
      tipo: 'incerto',
      titolo: 'Non so se la fattura è stata creata',
      spiega:
        d.detail ||
        'La richiesta è partita ma non ho ricevuto conferma. Controlla su ' +
          'Fatture in Cloud prima di riprovare: se c’è già, riprovare ne ' +
          'creerebbe una seconda.',
      azione: 'Apri Fatture in Cloud',
      riprova: false,
    };
  }

  // ── ① RIUSCITA. `verified` e `created` sono due gradi della stessa cosa:
  // il documento su FIC esiste. Lo stato `error` arriva con un 200 — il
  // servizio non solleva quando è FIC a bocciare — quindi il 200 da solo NON
  // significa riuscita, ed è la trappola di questo endpoint.
  if (status >= 200 && status < 300) {
    if (d.stato === 'error') {
      // ⛔ IL DOCUMENTO ESISTE GIÀ: non si può dire «non è andata», e non si
      // può offrire il riprova. 01/09/2026, visto in produzione: la verifica
      // formale è fallita DOPO la creazione, e all'artigiano è arrivato «Non
      // sono riuscito a inviarla» mentre il documento era su Fatture in Cloud.
      // Un artigiano che legge «non è andata» ripreme — ed è così che si fanno
      // DUE fatture, la cosa da cui il quarto esito ci difende.
      // 📌 Stessa forma del quarto esito, applicata al caso che non copriva:
      // lì NON sapevamo se esistesse; qui lo SAPPIAMO, e stavamo dicendo il
      // contrario.
      if (d.fic_document_id) {
        return {
          tipo: 'incerto',
          titolo: 'La fattura è su Fatture in Cloud, ma va controllata',
          spiega:
            `La fattura è stata creata su Fatture in Cloud (rif. ${d.fic_document_id}). ` +
            (d.ultimo_errore
              ? `Non è però completa: ${d.ultimo_errore} `
              : 'Non sono riuscito a completare la verifica formale. ') +
            'Correggila lì: non riemetterla, ne creeresti una seconda.',
          azione: 'Apri su Fatture in Cloud',
          url: urlDocumentoFIC(d.fic_document_id),
          riprova: false,
        };
      }
      return {
        tipo: 'bloccata',
        titolo: 'Fatture in Cloud non l’ha accettata',
        spiega: d.ultimo_errore || 'Controlla i dati della fattura e riprova.',
        azione: 'Riprova',
        riprova: true,
      };
    }
    return {
      // ⛔ IL VERBO CONTA, ed è la voce [[DBT-115]]: non «fatto», non
      // «inviata». Il documento nasce su FIC «non firmata e non inviata» e
      // allo SdI non ci va nessuno, perché la Fase 2b ([[FAT-7]]) non esiste.
      // Il titolo precedente — «Inviata a Fatture in Cloud» — era ONESTO come
      // nome (è esattamente ciò che avevamo fatto) e l'artigiano lo leggeva
      // come «ho finito», mentre gli restava un passo che nessuno gli aveva
      // nominato. Uno stato vero che, letto da chi non conosce il meccanismo,
      // significa un'altra cosa.
      //
      // ⚠️ PROVVISORIO E DICHIARATO TALE: è un ponte verso [[FAT-7]]. Il
      // giorno che mAIstro trasmette allo SdI da solo, questo bottone va
      // TOLTO — non lasciato lì a invitare in un posto dove non serve più
      // andare.
      //
      // ⚪ LIMITE: il numero che FIC assegna al documento (es. «2/2026») NON
      // è nostro — `DocumentoFIC.numero` è il numero di mAIstro. Mostriamo il
      // riferimento che possediamo invece di inventarne uno leggibile.
      tipo: 'riuscita',
      titolo: 'Fattura creata su Fatture in Cloud',
      spiega: d.fic_document_id
        ? `Documento creato su Fatture in Cloud (rif. ${d.fic_document_id}). `
          + 'Per firmarla e inviarla allo SdI, completala lì.'
        : 'Documento creato su Fatture in Cloud. Per firmarla e inviarla allo '
          + 'SdI, completala lì.',
      azione: 'Apri su Fatture in Cloud',
      url: urlDocumentoFIC(d.fic_document_id),
      riprova: false,
    };
  }

  // ── ② BLOCCATA: non è partito niente, e il messaggio dice cosa fare.
  if (status === 409) {
    // ⛔ Il plafond e' l'UNICO muro che porta dei numeri: il backend sa quanti
    // documenti sono stati usati, la mappa no. Per gli altri due la copia
    // resta quella scritta qui — non si lascia al backend il testo che
    // l'artigiano legge, tranne dove il testo contiene una misura.
    if (d.codice === PLAFOND_DOCUMENTI) {
      return {
        ...BLOCCHI[PLAFOND_DOCUMENTI],
        spiega: d.detail || BLOCCHI[PLAFOND_DOCUMENTI].spiega,
        url: URL_FIC_DOCUMENTI,
      };
    }
    const noto = BLOCCHI[d.codice];
    if (noto) return { ...noto, spiega: noto.spiega };
    return {
      tipo: 'bloccata',
      titolo: 'Non posso inviarla così',
      spiega: d.detail || 'Manca un dato per emettere la fattura.',
      azione: 'Riprova',
      riprova: true,
    };
  }

  // ── ③ ERRORE DI FIC / rete: qui il riprova è sicuro, perché non è partito
  // nulla (il rate limit e i guasti a monte non creano documenti).
  if (status === 429) {
    return {
      tipo: 'errore',
      titolo: 'Fatture in Cloud è occupato',
      spiega: 'Troppe richieste in poco tempo. Riprova fra qualche secondo.',
      azione: 'Riprova',
      riprova: true,
    };
  }

  return { ...RIPIEGO, spiega: d.detail || 'Riprova fra poco.' };
}

/** L'etichetta della LISTA — e dice cosa abbiamo fatto NOI, non cosa c'è su FIC. */
/**
 * Lo stato di emissione su FIC letto DALLA FATTURA, non dallo stato del pannello.
 *
 * ⛔ IL DIFETTO CHE CHIUDE ([[DBT-122]], trovato da Vale usando il prodotto il
 * 02/09): il pannello dell'esito viveva in `useState`, quindi alla ricarica
 * l'esito era `null` — il bottone «Emetti su Fatture in Cloud» tornava ATTIVO
 * su una fattura già emessa, e il messaggio «completala lì» col suo
 * collegamento sparivano **esattamente quando servono: il giorno dopo**.
 *
 * 📌 Il messaggio diventa una PROPRIETÀ DELLA FATTURA, non di una sessione.
 * Il dato era già sul filo (`fic_stato_invio`, `fic_inviata_il`,
 * `fic_document_id`, tre sottoquery): nessuno lo guardava.
 *
 * ⚪ LIMITE DICHIARATO: il numero che FIC assegna al documento («4/2026») NON
 * è nostro — non lo salviamo da nessuna parte. Mostriamo il riferimento che
 * possediamo invece di inventarne uno leggibile. Per mostrare il numero vero
 * servirebbe persisterlo, ed è una voce a parte.
 *
 * @param {object} fattura  con `fic_stato_invio` / `fic_document_id`
 * @returns {{emessa: boolean, url: string|null, titolo: string, spiega: string}|null}
 */
export function statoEmissioneFIC(fattura) {
  const f = fattura || {};
  const stato = f.fic_stato_invio;
  // `pending` non è emessa: il documento non esiste ancora su FIC.
  // `error` SÌ, se c'è l'id — è il caso di FT-2026-030: creata e non verificata.
  const emessa = Boolean(
    (stato === 'created' || stato === 'verified' || stato === 'error')
      && f.fic_document_id,
  );
  if (!emessa) return null;
  return {
    emessa: true,
    url: urlDocumentoFIC(f.fic_document_id),
    titolo: 'Creata su Fatture in Cloud',
    spiega:
      `Documento creato su Fatture in Cloud (rif. ${f.fic_document_id}). `
      + 'Per firmarla e inviarla allo SdI, completala lì.',
    azione: 'Apri su Fatture in Cloud',
  };
}

export function etichettaListaFIC(statoInvio) {
  if (!statoInvio) return { testo: 'Da emettere', tono: 'neutro' };
  if (statoInvio === 'error') return { testo: 'Bloccata', tono: 'errore' };
  if (statoInvio === 'pending') return { testo: 'In corso', tono: 'attesa' };
  return { testo: 'Inviata a FIC', tono: 'ok' };
}

/**
 * Il bottone «Riprova» si mostra? — la condizione ESTRATTA dal JSX.
 *
 * ⛔ PERCHÉ È QUI E NON DENTRO IL COMPONENTE: era una riga di JSX, e una riga
 * di JSX non si può asserire senza montare la schermata. La prova che serviva
 * è NEGATIVA — «quando il documento esiste, il bottone NON c'è» — ed è il tipo
 * di riga che protegge una cura quando qualcuno «migliorerà» la schermata fra
 * sei mesi.
 *
 * 🔴 SERVE PERCHÉ LA CURA SI È TOLTA DA SOLA IL MODO DI MANIFESTARSI: il
 * difetto del 01/09 si raggiungeva attraverso un `ValueError` in `xml_verify`,
 * e quel `ValueError` è stato chiuso. Adesso quel percorso non si raggiunge più
 * dalla strada che l'aveva rivelato: senza un'asserzione negativa, la cura
 * resta viva solo finché nessuno tocca il ramo.
 *
 * @param {object} esito   l'esito calcolato da `esitoEmissione`
 * @param {string} codice  il `codice` restituito dal backend (i due muri)
 */
export function mostraBottoneRiprova(esito, codice) {
  if (!esito) return false;
  return Boolean(
    esito.riprova
      && esito.tipo !== 'incerto'
      && codice !== MURO_DATO_MANCANTE
      && codice !== MURO_FUORI_APP,
  );
}

/**
 * Il bottone «Emetti su Fatture in Cloud» si mostra? — condizione ESTRATTA.
 *
 * ⛔ ESTRATTA PER LA STESSA RAGIONE DI `mostraBottoneRiprova`: una riga di JSX
 * non si può asserire senza montare la schermata, e la prova che serve è
 * NEGATIVA — «su una fattura già emessa il bottone NON c'è».
 *
 * 🔴 E stavolta la lezione arriva dal perimetro dell'altra: quella prova
 * sorveglia «Riprova», che compare DOPO UN ERRORE. Questo bottone compare
 * SEMPRE. Avevamo protetto la porta che si apre quando va male, e lasciato
 * aperta quella che è lì tutti i giorni ([[DBT-122]]).
 *
 * @param {object|null} esito  l'esito della sessione corrente, se c'è
 * @param {object|null} gia    `statoEmissioneFIC(fattura)`
 */
export function mostraBottoneEmetti(esito, gia) {
  return !esito && !gia;
}
